import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged, ValueOf } from 'type-fest';
import { dayjs } from '../../../../shared/utils/date';
import { SystemMutableEntity as SME } from '../../common';
import { UniqueShortId } from '../../common/id';
import { ImportTableDefinition } from '../../import/importTableDefinition';
import { ImportTableDefinitionRevision } from '../../import/importTableDefinitionRevision';
import { ImportTask } from '../../import/importTask';
import { Customer } from '../customer';

import Project = AuthDomain.Object.Project;

import type { SystemCommandContext } from '../common';

const uuidGenerator = UniqueShortId.GetFactory(21);

const _Status = {
  Active: 'Active',
  Inactive: 'Inactive',
  Cancelled: 'Cancelled',
} as const;
type _Status = ValueOf<typeof _Status>;

export type Contract = SME & {
  id: Contract.Id;
  projectId: Project.Id;
  customerId: Customer.Id;

  /** 所有する ImportTableDefinition（projectId + importTableDefinitionId + contractId で一意） */
  importTableDefinitionId: ImportTableDefinition.Id;

  /** テーブル定義スコープにおける契約ID */
  contractId: string;

  status: _Status;
  startDate: dayjs | undefined;
  endDate: dayjs | undefined;
  metadata: Record<string, unknown> | undefined;

  /** 最後にこのレコードを投入/更新した ImportTableDefinitionRevision の ID */
  lastImportTableDefinitionRevisionId: ImportTableDefinitionRevision.Id | undefined;
  /** 最後にこのレコードを投入/更新した ImportTask の ID */
  lastImportTaskId: ImportTask.Id | undefined;
};

export namespace Contract {
  export type Status = _Status;
  export const Status = _Status;

  export type Id = Tagged<UniqueShortId, 'contractInternalId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type Props = {
    id: string;
    projectId: string;
    customerId: string;
    importTableDefinitionId: string;
    contractId: string;
    status: Status;
    startDate: string | Date | dayjs | undefined;
    endDate: string | Date | dayjs | undefined;
    metadata: Record<string, unknown> | undefined;
    lastImportTableDefinitionRevisionId: string | undefined;
    lastImportTaskId: string | undefined;
  } & SME.Props;
  export const Construct = (props: Props): Contract => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      customerId: Customer.Id.FromString(props.customerId),
      importTableDefinitionId: ImportTableDefinition.Id.FromString(props.importTableDefinitionId),
      contractId: props.contractId,
      status: props.status,
      startDate: props.startDate ? dayjs(props.startDate) : undefined,
      endDate: props.endDate ? dayjs(props.endDate) : undefined,
      metadata: props.metadata,
      lastImportTableDefinitionRevisionId: props.lastImportTableDefinitionRevisionId
        ? ImportTableDefinitionRevision.Id.FromString(props.lastImportTableDefinitionRevisionId)
        : undefined,
      lastImportTaskId: props.lastImportTaskId ? ImportTask.Id.FromString(props.lastImportTaskId) : undefined,
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Contract.Props, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>;
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): Contract => {
    const { context, props } = params;
    const { projectId, now } = context;
    return Construct({
      id: Id.Generate(),
      projectId,
      ...props,
      ...SME.Create({ date: now }),
    });
  };

  export namespace Update {
    export type Props = Partial<
      Pick<Contract, 'status' | 'startDate' | 'endDate' | 'metadata' | 'lastImportTableDefinitionRevisionId' | 'lastImportTaskId'>
    >;
    export type Params = {
      context: SystemCommandContext;
      cur: Contract;
      props: Update.Props;
    };
  }
  export const Update = (params: Update.Params): Contract => {
    const { context, cur, props } = params;
    const { now } = context;
    return {
      ...cur,
      status: props.status ?? cur.status,
      startDate: props.startDate !== undefined ? props.startDate : cur.startDate,
      endDate: props.endDate !== undefined ? props.endDate : cur.endDate,
      metadata: props.metadata !== undefined ? props.metadata : cur.metadata,
      lastImportTableDefinitionRevisionId:
        props.lastImportTableDefinitionRevisionId !== undefined
          ? props.lastImportTableDefinitionRevisionId
          : cur.lastImportTableDefinitionRevisionId,
      lastImportTaskId: props.lastImportTaskId !== undefined ? props.lastImportTaskId : cur.lastImportTaskId,
      ...SME.Update({ cur, date: now }),
    };
  };
}
